import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Card, Divider, Button } from "antd"
import { ReloadOutlined, RobotOutlined } from "@ant-design/icons"
import type React from "react"
import AiFilterForm from "./components/AiFilterForm"
import AiLogTable from "./components/AiLogTable"
import AiLogDetailModal from "./components/AiLogDetailModal"
import { aiLogService, type AiLogQueryParams, type AiLogItem } from "@/services/log/aiLogApi"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"

export const AiLogPage: React.FC = () => {
  const [searchParams, setSearchParams] = useTableParams<AiLogQueryParams>({
    ...PAGE_CONFIG,
    search: "",
    action_type: undefined,
    model: undefined,
    start_date: undefined,
    end_date: undefined,
  })

  const [total, setTotal] = useState<number>(0)
  const [selectedRecord, setSelectedRecord] = useState<AiLogItem | null>(null)

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["ai_logs_list", searchParams],
    queryFn: () => {
      return aiLogService.list({
        page: searchParams.page,
        limit: searchParams.per_page,
        search: searchParams.search || undefined,
        action_type: searchParams.action_type || undefined,
        model: searchParams.model || undefined,
        start_date: searchParams.start_date || undefined,
        end_date: searchParams.end_date || undefined,
      })
    },
  })

  const handleSearch = (values: Omit<AiLogQueryParams, "page" | "limit">) => {
    setSearchParams({
      ...searchParams,
      ...values,
      page: 1,
    })
  }

  const items = result?.data || []
  const totalCount = result?.total ?? total

  return (
    <div className="flex flex-col gap-2">
      <AiFilterForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          search: searchParams.search,
          action_type: searchParams.action_type,
          model: searchParams.model,
          start_date: searchParams.start_date,
          end_date: searchParams.end_date,
        }}
      />

      <Card
        className="flex min-h-0 grow flex-col shadow-xs"
        classNames={{ body: "flex grow flex-col" }}
        title={
          <div className="flex items-center justify-between py-1">
            <div className="flex items-center gap-2">
              <RobotOutlined className="text-purple-600 text-lg" />
              <span className="font-semibold text-base text-gray-800">
                Nhật ký sử dụng AI (AI Usage Logs)
              </span>
              <Divider orientation="vertical" />
              <span className="font-normal text-gray-500 text-sm">
                Tổng số lượt gọi: <strong className="text-gray-700">{totalCount}</strong>
              </span>
            </div>
            <Button
              icon={<ReloadOutlined spin={isFetching} />}
              onClick={() => refetch()}
              loading={isFetching}
            >
              Làm mới
            </Button>
          </div>
        }
      >
        <AiLogTable
          data={items}
          loading={isFetching}
          onViewDetail={(record) => setSelectedRecord(record)}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total: totalCount,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>

      <AiLogDetailModal
        open={!!selectedRecord}
        record={selectedRecord}
        onClose={() => setSelectedRecord(null)}
      />
    </div>
  )
}

export default AiLogPage
